import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  Alert,
  AlertDescription,
  Badge,
  Button,
  DataTable,
  type DataTableColumnDef,
  Input,
  Switch,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { FormField, RenderFields, RestrictedBanner } from '~/components';
import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useCreateBankAccount, useUpdateBankAccount } from '~/lib/api/bankAccounts';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { BankAccountSelector, EntitySelector } from '~/lib/components/selectors';
import { useNavigate } from '~/lib/navigation';
import { AccountNumber, BankAccountRepository } from '~/repositories/BankAccountRepository';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { formatDateTime } from '~/util';

import { AccountNumberModal } from './_components/AccountNumberModal';
import { BankAccountSectionProps } from './Section';

// The create form doesn't set currency_code, so new accounts get the API default.
const DEFAULT_ACCOUNT_CURRENCY = 'USD';

const RESERVE_ACCOUNT_TYPES = ['OVERDRAFT_RESERVE', 'PROGRAM_RESERVE'];

interface Params {
  id: string;
}

interface AccountNumberRow extends AccountNumber {
  isDefaultAccountNumber: boolean;
}

const getAccountNumberColumns = (
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<AccountNumberRow>[] => [
  {
    header: 'Description',
    accessor: 'description',
    width: 'minmax(min-content, 2fr)',
    cell: ({ row, value }) => (
      <DataTable.TextCell value={value} weight="medium">
        {row.isDefaultAccountNumber && <Badge variant="secondary">Default</Badge>}
      </DataTable.TextCell>
    ),
  },
  {
    header: 'Routing Number',
    accessor: 'routingNumber',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <DataTable.TextCell value={value} />,
  },
  {
    header: 'Account Number',
    accessor: 'accountNumber',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <DataTable.TextCell value={value} />,
  },
  {
    header: 'Account Number ID',
    accessor: 'id',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <DataTable.TextCell displayVariant="monospaced" value={value} />,
  },
  getTimestampColumn<AccountNumberRow>({
    accessor: 'createdAt',
    timeFormat,
    onTimeFormatChange,
    width: '100px',
    fallback: '-',
  }),
];

interface BankAccountForm {
  id?: string;
  createdAt?: Date;
  description: string;
  displayName: string;
  entityId: string;
  isOverdraftable: boolean;
  overdraftReserveAccountId: string;
}

const isValidDate = (value: unknown): value is Date => value instanceof Date && !isNaN(value.getTime());

const toFormValues = (bankAccount?: BankAccountSectionProps['bankAccount']): BankAccountForm => ({
  id: bankAccount?.id,
  createdAt: isValidDate(bankAccount?.createdAt) ? bankAccount.createdAt : undefined,
  description: bankAccount?.description ?? '',
  displayName: bankAccount?.displayName ?? '',
  entityId: bankAccount?.owners?.[0] ?? '',
  isOverdraftable: bankAccount?.isOverdraftable ?? false,
  overdraftReserveAccountId: bankAccount?.overdraftReserveAccountId ?? '',
});

export const PageBankAccountsEdit: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const queryClient = useQueryClient();
  const { cursor, onNext, setPageSize, ...pagination } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('account-numbers');
  const [modalState, setModalState] = useState<{ isOpen: boolean; accountNumber?: AccountNumber }>({ isOpen: false });

  const outletContext = useOutletContext<BankAccountSectionProps>();
  const bankAccount = outletContext?.bankAccount;
  const currency = bankAccount?.currencyCode ?? DEFAULT_ACCOUNT_CURRENCY;
  const isReserveAccount = RESERVE_ACCOUNT_TYPES.includes(bankAccount?.type ?? '');

  const createBankAccount = useCreateBankAccount();
  const updateBankAccount = useUpdateBankAccount();

  // The API only accepts an overdraft reserve account in the same currency as this account.
  const reserveAccountFilters = useMemo(() => ({ type: 'OVERDRAFT_RESERVE', currency_code: currency }), [currency]);
  const { bankAccounts: reserveAccounts, isLoading: isReserveAccountsLoading } = useBankAccountOptions({
    filters: reserveAccountFilters,
  });
  const hasReserveAccounts = reserveAccounts.length > 0;

  const formMethods = useForm<BankAccountForm>({ defaultValues: toFormValues(bankAccount) });
  const { handleSubmit, watch, reset, formState } = formMethods;
  const { isDirty } = formState;
  const { handleSubmitErrors } = useFormErrorHandler();
  const isOverdraftable = watch('isOverdraftable');

  useEffect(() => {
    if (!bankAccount) return;
    // reset (not setValue) so the loaded values become the pristine baseline; formState.isDirty
    // then reflects genuine user edits, which is what reveals the Save Changes button.
    reset(toFormValues(bankAccount));
  }, [bankAccount, reset]);

  const onSubmit = (data: BankAccountForm) => {
    if (!currentUser) return;
    if (id) {
      updateBankAccount.mutate(
        {
          path: { bank_account_id: id },
          body: {
            description: data.description,
            display_name: data.displayName,
            is_overdraftable: data.isOverdraftable,
            overdraft_reserve_account_id: data.isOverdraftable ? data.overdraftReserveAccountId : undefined,
          },
        },
        {
          onSuccess: (response) => {
            // Re-baseline to the saved values so the form is no longer dirty and the button hides.
            reset(data);
            addSuccessNotification({
              content:
                response.display_name || response.description
                  ? `${response.display_name || response.description} updated`
                  : 'Updated',
            });
          },
          onError: (error) => {
            addDangerNotification({ content: error.message ?? 'Failed to update bank account' });
          },
        }
      );
      return;
    }

    createBankAccount.mutate(
      {
        body: {
          entity_id: data.entityId,
          description: data.description,
          display_name: data.displayName,
          is_overdraftable: data.isOverdraftable,
          overdraft_reserve_account_id: data.isOverdraftable ? data.overdraftReserveAccountId : undefined,
        },
      },
      {
        onSuccess: (response) => {
          navigate(ROUTE.BANK_ACCOUNTS);
          addSuccessNotification({
            content:
              response.display_name || response.description
                ? `${response.display_name || response.description} created`
                : 'Created',
          });
        },
        onError: (error) => {
          addDangerNotification({ content: error.message ?? 'Failed to create bank account' });
        },
      }
    );
  };

  const hasUpdatePermissions = isAuthorized({
    permission: 'bankAccounts',
    permissionLevel: 'update',
    bankAccount: { id, owners: bankAccount?.owners },
  });

  const { data: accountNumbersData, isFetching: isAccountNumbersFetching } = useQuery({
    queryKey: ['bank-account-account-numbers', id, cursor, pagination.pageSize],
    queryFn: async () => {
      if (!id) {
        return { accountNumbers: [], hasMore: false };
      }
      return BankAccountRepository.getAllAccountNumber(id, {
        limit: pagination.pageSize,
        startingAfter: cursor,
      });
    },
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
  const accountNumberRows = useMemo(() => {
    return (accountNumbersData?.accountNumbers ?? []).map((accountNumber) => ({
      ...accountNumber,
      isDefaultAccountNumber: accountNumber.id === bankAccount?.defaultAccountNumberId,
    }));
  }, [accountNumbersData, bankAccount]);
  const showRoutingNumber = useMemo(() => {
    if (accountNumberRows.length === 0) return false;
    return !accountNumberRows.every((accountNumber) => accountNumber.routingNumber);
  }, [accountNumberRows]);

  const openCreateAccountNumber = () => setModalState({ isOpen: true });

  const cancelButton = (
    <Button onClick={() => navigate(-1)} variant="secondary" size="sm" type="button">
      Cancel
    </Button>
  );
  const createButton = (
    <Button
      onClick={handleSubmit(onSubmit, handleSubmitErrors)}
      disabled={!hasUpdatePermissions || createBankAccount.isPending}
      size="sm"
      type="button"
    >
      <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
      {createBankAccount.isPending ? 'Creating…' : 'Create Account'}
    </Button>
  );

  const reserveAccountNotice = isReserveAccount ? (
    <Alert>
      <AlertDescription>Platform reserve accounts can't be overdrafted.</AlertDescription>
    </Alert>
  ) : (
    !isReserveAccountsLoading &&
    !hasReserveAccounts && (
      <Alert className="flex items-center justify-between gap-4">
        <AlertDescription>
          {`Overdrafts need an overdraft reserve account in ${currency}. Create your root entity to set one up.`}
        </AlertDescription>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => navigate(currentPlatform?.isLiveEnabled ? ROUTE.PLATFORM : ROUTE.PLATFORM_ROOT_ENTITY)}
        >
          Create
        </Button>
      </Alert>
    )
  );

  return (
    <>
      <AccountNumberModal
        accountNumber={modalState.accountNumber}
        bankAccountId={id}
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ isOpen: false });
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['bank-account-account-numbers', id] });
        }}
      />
      {!id && (
        <PageActions>
          {cancelButton}
          {createButton}
        </PageActions>
      )}

      {!hasUpdatePermissions && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit, handleSubmitErrors)}>
          {RenderFields<BankAccountForm>({
            sections: [
              {
                headline: 'Information',
                fields: [
                  {
                    id: 'displayName',
                    label: 'Display Name',
                    children: ({ value, onChange, onBlur, ref }) => (
                      <Input
                        ref={ref}
                        placeholder="Display Name"
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    ),
                  },
                  {
                    id: 'description',
                    label: 'Description',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { error }) => (
                      <Input
                        ref={ref}
                        placeholder="Description"
                        aria-invalid={!!error}
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    ),
                  },
                  {
                    id: 'entityId',
                    label: 'Entity',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <EntitySelector
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        locked={Boolean(id)}
                        hasError={!!error}
                      />
                    ),
                  },
                  {
                    hide: !reserveAccountNotice,
                    content: <>{reserveAccountNotice}</>,
                  },
                  {
                    content: (
                      <>
                        <FormField<BankAccountForm>
                          id="isOverdraftable"
                          label="Overdraftable"
                          description="Lets the balance go negative, covered by an overdraft reserve account."
                        >
                          {({ value, onChange }) => (
                            <Switch
                              className="@tablet/form-field:mt-3"
                              checked={Boolean(value)}
                              onCheckedChange={onChange}
                              // Switching off stays allowed even when the account can't be made overdraftable.
                              disabled={!value && (isReserveAccount || !hasReserveAccounts)}
                            />
                          )}
                        </FormField>
                        <Accordion type="single" value={isOverdraftable ? 'reserve' : ''}>
                          {/* Widened so the accordion's clipping doesn't crop the selector's focus ring. */}
                          <AccordionItem value="reserve" className="-mx-1 -mb-1 border-b-0">
                            <AccordionContent className="px-1 pt-6 pb-1">
                              <FormField<BankAccountForm>
                                id="overdraftReserveAccountId"
                                label="Reserve Account"
                                rules={{ required: isOverdraftable }}
                              >
                                {({ value, onChange, onBlur }, { error }) => (
                                  <BankAccountSelector
                                    value={String(value ?? '')}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    hasError={!!error}
                                    filters={reserveAccountFilters}
                                  />
                                )}
                              </FormField>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </>
                    ),
                  },
                ],
              },
              {
                hide: !id,
                fields: [
                  {
                    id: 'id',
                    label: 'ID',
                    children: ({ value }) => (
                      <Input
                        variant="copy"
                        readOnly
                        showCopyButton
                        className="[&_input]:font-mono"
                        value={String(value ?? '')}
                      />
                    ),
                  },
                  {
                    id: 'createdAt',
                    label: 'Created',
                    children: ({ value }) => (
                      <Input
                        variant="copy"
                        readOnly
                        showCopyButton
                        value={isValidDate(value) ? formatDateTime(value, { timeZoneFormat: 'utc' }) : '-'}
                      />
                    ),
                  },
                ],
              },
            ],
          })}
          {id ? (
            <AnimatePresence initial={false}>
              {isDirty && (
                <motion.div
                  key="save-changes"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                  className="overflow-hidden"
                >
                  <div className="flex justify-end px-(--page-margin) py-6">
                    <Button type="submit" disabled={!hasUpdatePermissions || updateBankAccount.isPending} size="sm">
                      {updateBankAccount.isPending ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            <div className="flex items-start justify-between px-(--page-margin) py-6">
              {cancelButton}
              {createButton}
            </div>
          )}
        </form>
      </FormProvider>

      {id && (
        <div className="grid gap-6 px-(--page-margin) py-6 shadow-[0_-1px_0_var(--color-border)]">
          <div className="flex items-center justify-between gap-6">
            <h2 className="m-0 text-base leading-5 font-medium text-foreground">Account Numbers</h2>
            <Button size="sm" type="button" disabled={!hasUpdatePermissions} onClick={openCreateAccountNumber}>
              <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
              Create
            </Button>
          </div>

          <DataTable<AccountNumberRow>
            {...tableDefaults}
            columns={getAccountNumberColumns(timeFormat, onTimeFormatChange)}
            columnVisibility={{
              accountNumber: true,
              bankAccountId: true,
              bic: false,
              checkIssuingPositivePayRequired: false,
              createdAt: true,
              description: true,
              id: true,
              isDefaultAccountNumber: true,
              routingNumber: showRoutingNumber,
            }}
            data={accountNumberRows}
            pagination={{
              hasNext: accountNumbersData?.hasMore ?? false,
              hasPrevious: pagination.hasPrevious,
              onNext: () =>
                accountNumbersData?.accountNumbers.length
                  ? onNext({
                      cursor: accountNumbersData.accountNumbers[accountNumbersData.accountNumbers.length - 1].id,
                    })
                  : undefined,
              onPrevious: pagination.onPrevious,
              onPageSizeChange: setPageSize,
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
            }}
            isLoading={isAccountNumbersFetching}
            rowKey={(row) => row.id}
            emptyContent={
              <TableEmpty
                icon={<Icon.AnimationWallet />}
                title="No account numbers found"
                description="Create an account number to get started."
                action={
                  hasUpdatePermissions ? (
                    <Button size="sm" type="button" onClick={openCreateAccountNumber}>
                      <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
                      Create
                    </Button>
                  ) : undefined
                }
              />
            }
            rowAction={(row) => (
              <Button
                size="xs"
                variant="secondary"
                type="button"
                onClick={() => setModalState({ isOpen: true, accountNumber: row })}
              >
                <Icon.Edit className="[--icon-size-x:12px] [--icon-size-y:12px]" />
                Edit
              </Button>
            )}
          />
        </div>
      )}
    </>
  );
};
